import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  LogIn,
  Mail,
  Lock,
  User,
  Phone,
  Briefcase,
  Building2,
  Shield,
  Key,
  FileText,
  Plus,
  ArrowLeft,
} from "lucide-react";
import useAuth from "../useAuth";
import logo from "../assets/logo.jpg";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import policyOptions from "../jsons/policyOptions.json";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false); // Toggle state

  // Registration State
  const [regFormData, setRegFormData] = useState({
    password: "",
    fullName: "",
    mobileNo: "",
    email: "",
    designation: "",
    userType: "",
    vlanId: "20",
    receivedDepartment: "Admin",
    policy: "",
    key: "",
    isReadOnly: false,
    isVerified: true,
    departmentId: null,
  });
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departmentsList, setDepartmentsList] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const userTypes = ["Minister", "Secretary", "Director", "Others"];

  const { user, authMessage, loading, loginWithEmailAndPassword } = useAuth();

  // Fetch Departments for Registration
  useEffect(() => {
    if (isRegister) {
      const fetchDepartments = async () => {
        try {
          // Note: This endpoint might require auth. If so, this will fail for unauth users.
          // Proceeding as requested to mirror RegisterUser logic.
          const token = localStorage.getItem("dbToken");
          const response = await axios.get(`${server}/department/getAll`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            params: { pageNo: 1, pageSize: 1000 },
          });
          if (response.data.success) {
            setDepartmentsList(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching departments:", error);
          // toast.error("Failed to load departments."); // Suppress error for cleaner UX if it fails
        }
      };
      fetchDepartments();
    }
  }, [isRegister]);

  // Login Handler
  const handleLogin = async (e) => {
    e.preventDefault();
    loginWithEmailAndPassword(email, password);
  };

  // Registration Handlers
  const handleRegChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUserTypeChange = (e) => {
    const value = e.target.value;
    setRegFormData((prev) => ({
      ...prev,
      userType: value,
    }));
  };

  const handleDepartmentChange = (e) => {
    const deptName = e.target.value;
    setSelectedDepartment(deptName);
    const deptObj = departmentsList.find((d) => d.departmentName === deptName);
    if (deptObj) {
      setRegFormData((prev) => ({
        ...prev,
        receivedDepartment: deptObj.departmentName,
        vlanId: deptObj.vlanId,
        departmentId: deptObj.id,
      }));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);

    const payload = {
      username: String(regFormData.mobileNo),
      password: regFormData.password,
      fullName: regFormData.fullName,
      phoneNumber: String(regFormData.mobileNo),
      email: regFormData.email,
      department: regFormData.receivedDepartment,
      policiesMapperId: regFormData.policy,
      userType: regFormData.userType,
      vlanId: String(regFormData.vlanId),
      isVerified: regFormData.isVerified,
      departmentId: regFormData.departmentId,
    };

    try {
      const token = localStorage.getItem("dbToken");

      const response = await axios.post(`${server}/wifiusers/new`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Saved!");
        setRegFormData({
          password: "",
          fullName: "",
          mobileNo: "",
          designation: "",
          userType: "",
          vlanId: "20",
          receivedDepartment: "Admin",
          policy: "",
          key: "",
          isReadOnly: false,
          isVerified: true,
          departmentId: null,
        });
        setSelectedDepartment("");
        setIsRegister(false); // Switch back to login
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred during registration.",
      );
    } finally {
      setRegLoading(false);
    }
  };

  if (user) {
    return <Navigate to="/my/devices" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
      {/* Brand Section */}
      <div className="text-center mb-8">
        <div className="h-20 w-20 bg-white rounded-2xl shadow-sm border border-gray-200 flex items-center justify-center mx-auto mb-4 overflow-hidden p-2">
          <img src={logo} alt="Logo" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          DoIT Infrastructure
        </h1>
        <p className="text-sm text-gray-500 mt-1">Network Management System</p>
      </div>

      {/* Card Container */}
      <div
        className={`w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 ${isRegister ? "max-w-4xl" : "max-w-md"}`}
      >
        {!isRegister ? (
          // LOGIN FORM
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">Welcome back</h2>
              <p className="text-sm text-gray-500 mt-1">
                Please sign in to access your account
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  Mobile No / Username
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your username"
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Password
                  </label>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {authMessage && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100 flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                  {authMessage?.slice(5).split("-").join(" ")}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-white transition-all shadow-sm mt-2 ${
                  loading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Need WiFi access?{" "}
                <button
                  onClick={() => setIsRegister(true)}
                  className="text-blue-600 font-semibold hover:text-blue-700 hover:underline transition-all"
                >
                  Register Here
                </button>
              </p>
            </div>
          </>
        ) : (
          // REGISTER FORM
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  WiFi Registration
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Register to get WiFi credentials
                </p>
              </div>
              <button
                onClick={() => setIsRegister(false)}
                className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-all"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </button>
            </div>

            <form onSubmit={handleRegister} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Full Name
                  </label>
                  <div className="relative group">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      name="fullName"
                      value={regFormData.fullName}
                      onChange={handleRegChange}
                      placeholder="Enter full name"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                  </div>
                </div>

                {/* Mobile No */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Mobile No / Username
                  </label>
                  <div className="relative group">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      name="mobileNo"
                      value={regFormData.mobileNo}
                      onChange={handleRegChange}
                      placeholder="Enter mobile no"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                      maxLength={10}
                      onInput={(e) => {
                        if (e.target.value.length > 10)
                          e.target.value = e.target.value.slice(0, 10);
                      }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Email
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={regFormData.email}
                      onChange={handleRegChange}
                      placeholder="Enter email"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="password"
                      name="password"
                      value={regFormData.password}
                      onChange={handleRegChange}
                      placeholder="Create a password"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                      required
                    />
                  </div>
                </div>

                {/* Designation */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Designation
                  </label>
                  <div className="relative group">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      name="designation"
                      value={regFormData.designation}
                      onChange={handleRegChange}
                      placeholder="Designation"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* User Type */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    User Type
                  </label>
                  <div className="relative group">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      name="userType"
                      value={regFormData.userType}
                      onChange={handleUserTypeChange}
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                      required
                    >
                      <option value="" disabled>
                        Select Type
                      </option>
                      {userTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Department Selection */}
                <div className="space-y-2 md:col-span-2 bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <label className="text-xs font-bold text-blue-700 uppercase tracking-wider block">
                    Select Department
                  </label>
                  <div className="relative group mt-2">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400" />
                    <select
                      value={selectedDepartment}
                      onChange={handleDepartmentChange}
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                      required
                    >
                      <option value="" disabled>
                        Select Department
                      </option>
                      {departmentsList.map((dept, index) => (
                        <option key={index} value={dept.departmentName}>
                          {dept.departmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* VLAN ID */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    VLAN ID
                  </label>
                  <div className="relative group">
                    <input
                      type="number"
                      name="vlanId"
                      value={regFormData.vlanId}
                      readOnly
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono font-medium text-gray-500 cursor-not-allowed focus:outline-none"
                    />
                  </div>
                </div>

                {/* Policy */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Policy
                  </label>
                  <div className="relative group">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      name="policy"
                      value={regFormData.policy}
                      onChange={handleRegChange}
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                      required
                    >
                      <option value="" disabled>
                        Select Policy
                      </option>
                      {policyOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Key */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                    Key
                  </label>
                  <div className="relative group">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      name="key"
                      value={regFormData.key}
                      onChange={handleRegChange}
                      placeholder="Key"
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder-gray-400"
                    />
                  </div>
                </div>
                {/* Is Verified Toggle */}
                <div className="md:col-span-2 space-y-2">
                  <div
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() =>
                      setRegFormData((prev) => ({
                        ...prev,
                        isVerified: !prev.isVerified,
                      }))
                    }
                  >
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        regFormData.isVerified
                          ? "bg-blue-600 border-blue-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      {regFormData.isVerified && (
                        <span className="text-white text-xs font-bold">✓</span>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900 select-none">
                        Is Verified
                      </span>
                      <p className="text-xs text-gray-500 select-none">
                        Toggle to mark user as verified
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={regLoading}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-white transition-all shadow-sm ${
                  regLoading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                    : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 hover:shadow"
                }`}
              >
                {regLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Registering...</span>
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    <span>Register</span>
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="mt-8 text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} DoIT Infrastructure. All rights
        reserved.
      </div>
    </div>
  );
}
